import { parsePhoneNumberWithError } from "libphonenumber-js";
import {
  hasImplausibleDigitPattern,
  isDisposableEmail,
} from "./personalDataRules";

/**
 * Structural email validation. Intentionally pragmatic (not RFC-exhaustive):
 * we want to catch the obvious "this can never receive mail" mistakes while
 * still accepting normal addresses. Checks:
 *  - exactly one "@", non-empty local and domain parts
 *  - no whitespace anywhere
 *  - no consecutive / leading / trailing dots
 *  - domain has at least two labels and a letters-only TLD of >= 2 chars
 *
 * Unicode letters are allowed in the local part and domain labels so we do not
 * reject legitimate accented addresses (e.g. "josé@müller.de") that the old,
 * looser check accepted — the goal is to catch typos, not to narrow the set of
 * valid addresses.
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  if (/\s/.test(email)) return false;
  if (email.includes("..")) return false;

  const parts = email.split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  if (local.length === 0 || domain.length === 0) return false;

  // Local part: any unicode letter/number plus the common mail specials.
  if (local.startsWith(".") || local.endsWith(".")) return false;
  if (!/^[\p{L}\p{N}.!#$%&'*+/=?^_`{|}~-]+$/u.test(local)) return false;

  // Domain part
  if (domain.startsWith(".") || domain.endsWith(".")) return false;
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (label.length === 0) return false;
    if (!/^[\p{L}\p{N}-]+$/u.test(label)) return false;
    if (label.startsWith("-") || label.endsWith("-")) return false;
  }
  // TLD: letters only (unicode allowed), >= 2 chars. Rejects digit TLDs like
  // ".c0m" but accepts ".de", ".com", ".рф", ".co.uk" (last label "uk").
  const tld = labels[labels.length - 1];
  return /^\p{L}{2,}$/u.test(tld);
};

/**
 * Normalise an email address before validation / submission:
 *  - strip control characters and zero-width / BOM characters that sneak in
 *    via copy-paste
 *  - trim surrounding whitespace
 *  - lowercase (email is treated case-insensitively for delivery)
 */
export const cleanEmail = (email: string): string =>
  email
    // C0 controls + DEL + C1 controls, zero-width chars and BOM
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF]/g, "")
    .trim()
    .toLowerCase();

/**
 * Top-level-domain typos mapped to their correction. These corrections apply
 * to ANY domain, so the list is restricted to strings that are not real TLDs
 * a customer of ours would legitimately use.
 *
 * Note on "cm": this IS a real ccTLD (Cameroon), but in our DE/AT consumer
 * market it is virtually always a typo of ".com" and is explicitly called out
 * in the ticket. "co" (Colombia) is deliberately NOT included — it is commonly
 * used by businesses and would cause false positives.
 */
const TLD_TYPOS: Record<string, string> = {
  con: "com",
  cmo: "com",
  ocm: "com",
  vom: "com",
  xom: "com",
  comm: "com",
  cpm: "com",
  cm: "com",
  dee: "de",
  d: "de",
  nett: "net",
  orgg: "org",
};

/**
 * Exact, hand-curated misspellings of well-known full domains. We match the
 * WHOLE domain exactly (not fuzzily) so there are zero false positives: valid
 * domains that merely resemble a popular one — e.g. "gmx.at", "gmx.ch",
 * "hotmail.at", "email.com" — are never touched. Add new entries as real
 * misspellings are observed; do NOT switch to edit-distance matching, which
 * would block legitimate ccTLD variants.
 */
const DOMAIN_TYPOS: Record<string, string> = {
  // gmail.com
  "gmal.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmsil.com": "gmail.com",
  "gmaul.com": "gmail.com",
  "gmali.com": "gmail.com",
  // googlemail.com
  "googlemai.com": "googlemail.com",
  "googlmail.com": "googlemail.com",
  // hotmail.com / .de
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmil.com": "hotmail.com",
  "hotmaill.com": "hotmail.com",
  "hotmial.de": "hotmail.de",
  "hotmal.de": "hotmail.de",
  // outlook.com / .de
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "outook.com": "outlook.com",
  "outlok.de": "outlook.de",
  // icloud.com
  "iclod.com": "icloud.com",
  "iclould.com": "icloud.com",
  // yahoo.com
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yhoo.com": "yahoo.com",
  // web.de
  "wweb.de": "web.de",
  // ".co" is a real TLD (Colombia) so we never touch it generically, but for
  // these well-known providers a ".co" address is virtually always a dropped-m
  // typo of ".com"/".de". Exact-match only, so generic "*.co" stays untouched.
  "gmail.co": "gmail.com",
  "googlemail.co": "googlemail.com",
  "hotmail.co": "hotmail.com",
  "outlook.co": "outlook.com",
  "yahoo.co": "yahoo.com",
  "icloud.co": "icloud.com",
  "live.co": "live.com",
  "aol.co": "aol.com",
  "gmx.co": "gmx.de",
  "web.co": "web.de",
};

/**
 * Suggests a likely-correct email address when the entered one is an exact,
 * high-confidence typo of a well-known domain or carries an obviously wrong
 * TLD. Returns the corrected address, or null when nothing obvious is wrong.
 *
 * Deliberately conservative: it only corrects (a) exact curated full-domain
 * misspellings and (b) non-real TLDs. It will MISS some typos rather than risk
 * blocking a valid address — ambiguous cases are handled by the soft
 * "suspicious" warning, not here.
 */
export const getEmailSuggestion = (email: string): string | null => {
  const cleaned = cleanEmail(email);
  // Work on minimally-parseable input only (one "@", non-empty local/domain,
  // a dot in the domain). We deliberately do NOT require full structural
  // validity so we can still suggest a fix for typos like "gmx.d" -> "gmx.de"
  // that fail the TLD-length check.
  if (/\s/.test(cleaned)) return null;
  const parts = cleaned.split("@");
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  if (local.length === 0 || domain.length === 0) return null;
  if (!domain.includes(".")) return null;

  // 1) exact curated full-domain misspelling
  const fixedDomain = DOMAIN_TYPOS[domain];
  if (fixedDomain && fixedDomain !== domain) {
    return `${local}@${fixedDomain}`;
  }

  // 2) non-real TLD typo (applies to any domain)
  const labels = domain.split(".");
  const tld = labels[labels.length - 1];
  const fixedTld = TLD_TYPOS[tld];
  if (fixedTld && fixedTld !== tld) {
    labels[labels.length - 1] = fixedTld;
    return `${local}@${labels.join(".")}`;
  }

  return null;
};

/**
 * Local-part prefixes that almost never belong to a real address and usually
 * indicate an accidental copy/autofill artefact prepended to the address
 * (e.g. "explorer.firstname.lastname@gmail.com"). Kept deliberately narrow to
 * avoid false positives on legitimate "firstname.lastname@" addresses.
 */
const SUSPICIOUS_LOCAL_PREFIXES = [
  "explorer",
  "internet",
  "mozilla",
  "safari",
  "chrome",
  "firefox",
  "iphone",
  "ipad",
  "android",
  "samsung",
];

/**
 * Flags structurally valid addresses whose local part begins with a known
 * accidental prefix. This is a soft signal — callers should warn, not block.
 */
export const hasSuspiciousEmailLocalPart = (email: string): boolean => {
  const cleaned = cleanEmail(email);
  const atIndex = cleaned.indexOf("@");
  if (atIndex <= 0) return false;
  const local = cleaned.slice(0, atIndex);
  return SUSPICIOUS_LOCAL_PREFIXES.some((prefix) =>
    local.startsWith(`${prefix}.`)
  );
};

/**
 * The gating check used to enable/continue a booking: the address must be
 * structurally valid AND free of any obvious typo we could suggest a fix for.
 * Suspicious local parts are intentionally NOT blocked here (warning only).
 */
export const isEmailAcceptable = (email: string): boolean => {
  const cleaned = cleanEmail(email);
  return (
    isValidEmail(cleaned) &&
    getEmailSuggestion(cleaned) === null &&
    // Disposable/throwaway domains are rejected wherever email is required
    // (rigorous personal-data rules — see util/personalDataRules.ts).
    !isDisposableEmail(cleaned)
  );
};

export const isValidUscCustomerId = (uscCustomerId: string) =>
  /^[0-9]{8,10}$/.test(uscCustomerId);

/**
 * We only test that the length is reasonable.
 * Additionally, we also ensure that a German phone number does not also include the local 0-prefix (so no 490<number>)
 * Input libphonenumber cannot parse (TOO_LONG, NOT_A_NUMBER, …) is invalid,
 * never an uncaught exception: the field then shows its validation message.
 * @param phone
 */
export const isValidPhone = (phone: string) => {
  if (phone.length > 9) {
    let phoneNumber;
    try {
      phoneNumber = parsePhoneNumberWithError(phone);
    } catch {
      return false;
    }

    // Validate German numbers (due to incosistency in the lib)
    if (phoneNumber.country === "DE") {
      if (
        phoneNumber.nationalNumber.length >= 10 &&
        phoneNumber.nationalNumber.length <= 11
      ) {
        return phoneNumber.isValid();
      }
      return false;
    }
    return phoneNumber.isValid();
  }
  return false;
};

/**
 * Plausibility check on top of `isValidPhone` (rigorous personal-data rules):
 * rejects structurally valid numbers whose national part is an obvious dummy
 * pattern — all-same-digit or a repeated 2–3 digit cycle like
 * "+49 1231 2312312" or "9090 909090". Callers should combine it with
 * `isValidPhone`; on a parse error this defers (returns true) since
 * `isValidPhone` already rejects unparseable input.
 */
export const isPlausiblePhone = (phone: string): boolean => {
  try {
    const phoneNumber = parsePhoneNumberWithError(phone);
    return !hasImplausibleDigitPattern(phoneNumber.nationalNumber);
  } catch {
    return true;
  }
};
